import { StepProps, Specialization, License } from '@/types/consultant-registration.types';
import { FormField } from '@/components/broker-registration/FormField';
import { TextInput } from '@/components/broker-registration/TextInput';
import { RadioGroup } from '@/components/broker-registration/RadioGroup';
import { SpecializationBlock } from './SpecializationBlock';
import { LicenseBlock } from './LicenseBlock';
import { useLanguage } from '@/contexts/LanguageContext';

export function StepTwo({ formData, updateFormData, errors, clearError }: StepProps) {
  const { t } = useLanguage();

  const addSpecialization = () => {
    if (formData.specializations.length < 3) {
      const newSpecializations: Specialization[] = [
        ...formData.specializations,
        { industry: '', expertises: [], experienceYears: '' },
      ];
      updateFormData({ specializations: newSpecializations });
    }
  };

  const removeSpecialization = (index: number) => {
    const newSpecializations = formData.specializations.filter((_, i) => i !== index);
    updateFormData({ specializations: newSpecializations });
  };

  const updateSpecialization = (index: number, updates: Partial<Specialization>) => {
    const newSpecializations = formData.specializations.map((spec, i) =>
      i === index ? { ...spec, ...updates } : spec
    );
    updateFormData({ specializations: newSpecializations });
  };

  const addLicense = () => {
    if (formData.licenses.length < 5) {
      const newLicenses: License[] = [
        ...formData.licenses,
        {
          details: '',
          number: '',
          jurisdictions: {
            canada: [],
            usa: [],
            europe: [],
            other: '',
          },
          showCanada: false,
          showUsa: false,
          showEurope: false,
        },
      ];
      updateFormData({ licenses: newLicenses });
    }
  };

  const removeLicense = (index: number) => {
    const newLicenses = formData.licenses.filter((_, i) => i !== index);
    updateFormData({ licenses: newLicenses });
  };

  const updateLicense = (index: number, updates: Partial<License>) => {
    const newLicenses = formData.licenses.map((license, i) =>
      i === index ? { ...license, ...updates } : license
    );
    updateFormData({ licenses: newLicenses });
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold border-b border-gray-700 pb-2 text-white">
        {t('consultant_registration.step_two.title')}
      </h2>

      {/* Error Summary */}
      {Object.keys(errors).length > 0 && (
        <div className="bg-red-500/20 border-2 border-red-500 rounded-lg p-4">
          <p className="text-red-300 font-semibold">
            {t('consultant_registration.step_two.errors_summary')}
          </p>
          <ul className="list-disc list-inside text-red-200 text-sm mt-2">
            {Object.values(errors).map((err, idx) => (
              <li key={idx}>{err}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Company Name (Optional) */}
      <FormField label={t('consultant_registration.step_two.company_name')}>
        <TextInput
          value={formData.companyName}
          onChange={(value) => updateFormData({ companyName: value })}
          placeholder={t('consultant_registration.step_two.company_name_placeholder')}
        />
      </FormField>

      {/* Business Sectors & Expertises */}
      <div className="mt-6">
        <h3 className="text-xl font-semibold text-white mb-4">
          {t('consultant_registration.step_two.sectors_title')}
        </h3>
        <p className="text-sm text-gray-400 mb-4">
          {t('consultant_registration.step_two.sectors_subtitle')}
        </p>

        {formData.specializations.map((spec, index) => (
          <SpecializationBlock
            key={index}
            specialization={spec}
            index={index}
            onUpdate={updateSpecialization}
            onRemove={removeSpecialization}
            errors={errors}
            clearError={clearError}
            canRemove={formData.specializations.length > 1}
          />
        ))}

        {formData.specializations.length < 3 && (
          <button
            type="button"
            onClick={addSpecialization}
            className="w-full mt-2 rounded border-2 border-[#CEAB65] text-[#CEAB65] px-4 py-3 text-sm font-medium hover:bg-[#CEAB65]/10 transition-colors"
          >
            {t('consultant_registration.step_two.add_sector')} ({3 - formData.specializations.length} remaining)
          </button>
        )}
      </div>

      {/* Licenses & Permits */}
      <div className="mt-8">
        <h3 className="text-xl font-semibold text-white mb-4">
          {t('consultant_registration.step_two.licenses_title')}
        </h3>

        <FormField
          label={t('consultant_registration.step_two.has_license')}
          error={errors.hasLicense}
          required
        >
          <RadioGroup
            name="hasLicense"
            value={formData.hasLicense}
            onChange={(value) => {
              updateFormData({ hasLicense: value as 'yes' | 'no' });
              if (value === 'yes' && formData.licenses.length === 0) {
                addLicense();
              }
              clearError('hasLicense');
            }}
            options={[
              { value: 'yes', label: t('consultant_registration.options.yes') },
              { value: 'no', label: t('consultant_registration.options.no') },
            ]}
            error={!!errors.hasLicense}
          />
        </FormField>

        {formData.hasLicense === 'yes' && (
          <>
            {formData.licenses.map((license, index) => (
              <LicenseBlock
                key={index}
                license={license}
                index={index}
                onUpdate={updateLicense}
                onRemove={removeLicense}
                errors={errors}
                clearError={clearError}
                canRemove={formData.licenses.length > 1}
              />
            ))}

            {formData.licenses.length < 5 && (
              <button
                type="button"
                onClick={addLicense}
                className="w-full mt-2 rounded border-2 border-blue-400 text-blue-400 px-4 py-3 text-sm font-medium hover:bg-blue-400/10 transition-colors"
              >
                {t('consultant_registration.step_two.add_license')} ({5 - formData.licenses.length} remaining)
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
